
import React, { useState } from "react";
import { Calculator } from "lucide-react";
import { calculateInvestmentValue, getCurrentAge } from "./calculator/utils";
import CalculatorInputPanel from "./calculator/CalculatorInputPanel";
import CalculatorResults from "./calculator/CalculatorResults";

const BirthDateCalculatorEn = () => {
  const [birthYear, setBirthYear] = useState<number>(1990);
  const [giftValue, setGiftValue] = useState<number>(7000);
  const [investmentPercentage, setInvestmentPercentage] = useState<number>(50);

  const currentAge = getCurrentAge(birthYear);
  const livretAAt18 = calculateInvestmentValue(birthYear, giftValue, investmentPercentage, 18, 0.015);
  const livretAAt25 = calculateInvestmentValue(birthYear, giftValue, investmentPercentage, 25, 0.015);
  const livretANow = calculateInvestmentValue(birthYear, giftValue, investmentPercentage, currentAge, 0.015);
  const legatiaAt18 = calculateInvestmentValue(birthYear, giftValue, investmentPercentage, 18, 0.08);
  const legatiaAt25 = calculateInvestmentValue(birthYear, giftValue, investmentPercentage, 25, 0.08);
  const legatiaNow = calculateInvestmentValue(birthYear, giftValue, investmentPercentage, currentAge, 0.08);

  const textContent = {
    title: "Investment Simulator",
    subtitle: "What if childhood gifts had been invested?",
    example: "Example:",
    bornIn: "Born in",
    giftsReceived: "received in gifts during childhood",
    investmentPart: "invested in Legatia",
    birthYear: "Your Birth Year",
    giftValue: "Total Childhood Gifts",
    investmentPercentage: "Percentage Invested",
    calculate: "Calculate",
    yourCapital: "Your Capital Today",
    livretA: "Livret A",
    legatia: "Legatia ETF",
    at18: "At 18",
    at25: "At 25",
    at30: "At 30",
    now: "Today",
    startInvesting: "Start Investing"
  };

  return (
    <section id="calculator-section" className="section-padding bg-gradient-to-b from-pink-50/20 via-purple-50/30 to-orange-50/20 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-orange-400/10 to-yellow-400/10 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex justify-center mb-6">
            <div className="bg-legatia-green p-4 rounded-full shadow-xl animate-pulse-slow">
              <Calculator className="h-8 w-8 text-white" />
            </div>
          </div>
          <div className="inline-block bg-legatia-green/20 rounded-full px-6 py-2 mb-6">
            <span className="font-semibold text-foreground">Impact Simulator</span>
          </div>
          <h2 className="section-title mb-4">
            The real cost of <span className="text-legatia-green font-bold">not investing</span> moments
          </h2>
          <p className="subtitle-hero">
            Calculate what your childhood gifts could have become if invested through Legatia
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-start">
            <CalculatorInputPanel 
              birthYear={birthYear} 
              giftValue={giftValue} 
              investmentPercentage={investmentPercentage} 
              onBirthYearChange={setBirthYear} 
              onGiftValueChange={setGiftValue} 
              onInvestmentPercentageChange={setInvestmentPercentage} 
              content={textContent} 
            />

            <CalculatorResults 
              content={textContent} 
              giftValue={giftValue} 
              currentAge={currentAge} 
              livretAAt18={livretAAt18} 
              livretAAt25={livretAAt25} 
              livretANow={livretANow} 
              legatiaAt18={legatiaAt18} 
              legatiaAt25={legatiaAt25} 
              legatiaNow={legatiaNow} 
            />
        </div>
        </div>
      </div>
    </section>
  );
};

export default BirthDateCalculatorEn;
